import React, { useState, useEffect, useRef } from 'react'

import LoadingSpinner from 'component/loading'
import NotFound from 'component/not-found'
import ContentSubmapelItem from './content-submapel-item'
import { useBaseContext } from 'context/base'

export default function ModalContentSubmapel({
  id,
  selectedData,
  onSave = () => { },
  onClose = () => { },
}) {
  /* ====================================== Consume Context ===================================== */
  const { getRequest } = useBaseContext()

  /* =========================================== Refs =========================================== */
  const closeBtn = useRef()

  /* ======================================= Local States ======================================= */
  const [sections, setSections] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedContent, setSelectedContent] = useState([])
  const [initialContent, setInitialContent] = useState([])
  const selectedContentRef = useRef(selectedContent)

  /* ========================================= Functions ======================================== */
  async function getSectionsByClassId(id) {
    try {
      setIsLoading(true)
      const response = await getRequest(`sections?class_id=${id}`)
      if (response) {
        setSections(response?.data || null)
      }
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * Fungsi untuk mengelola logika state seleksi checkbox hierarchy (Class -> Section -> Content).
   * State (selectedContent) akan dibentuk menjadi array seragam dengan struktur:
   * [ { id: class_id, data: [ { section_id: '...', contents_id: [...] } ] } ]
   */
  const setContentSelected = (payload) => {
    // payload membawa informasi checkbox mana yang memicu perubahan (type, IDs terkait, action checked)
    const { type, classId, sectionId, contentId, checked, sectionContents } = payload;

    setSelectedContent(prev => {
      // 1. Clone state sebelumnya untuk menghindari proses mutasi langsung (best practice React)
      let newState = [...prev];

      // 2. Cari / Inisialisasi object untuk level tertinggi, yakni Class (Mata pelajaran)
      let classIndex = newState.findIndex(c => c.id === classId);

      if (classIndex === -1) {
        if (!checked) return newState; // Jika event-nya uncheck dan data memang tidak ada, tidak ubah apa-apa
        // Jika check: buat entry parent baru untuk class ini
        newState.push({
          id: classId,
          data: []
        });
        classIndex = newState.length - 1; // Arahkan pointer ke index terbaru yang baru ditambahkan
      }

      // 3. Tarik array data section berdasarkan class yang baru didapat
      let classData = [...newState[classIndex].data];
      let sectionIndex = classData.findIndex(s => s.section_id === sectionId);

      /* ===================================================================================== */
      /* CASE A: JIKA YANG DI-KLIK ADALAH CHECKBOX "SECTION" (MEMPENGARUHI SELURUH CONTENT) */
      /* ===================================================================================== */
      if (type === 'section') {
        if (checked) {
          if (sectionIndex === -1) {
            // Jika centang section dan belum terdaftar: Daftarkan section_id, masukkan SEMUA contents_id-nya
            classData.push({
              section_id: sectionId,
              contents_id: sectionContents ? sectionContents.map(c => c.id) : []
            });
          } else {
            // Jika sudah ada (tapi mungkin checkbox-nya sebelumnya parsial/abu-abu),
            // pastikan SEMUA konten di dalam section dimasukkan/ditimpa.
            classData[sectionIndex] = {
              ...classData[sectionIndex],
              contents_id: sectionContents ? sectionContents.map(c => c.id) : []
            };
          }
        } else {
          // Jika centang section DIHAPUS, hilangkan referensi section dari array secara total
          if (sectionIndex !== -1) {
            classData.splice(sectionIndex, 1);
          }
        }

        /* ===================================================================================== */
        /* CASE B: JIKA YANG DI-KLIK ADALAH CHECKBOX "CONTENT" (HANYA MEMPENGARUHI CONTENT SPESIFIK) 
        /* ===================================================================================== */
      } else if (type === 'content') {
        if (checked) {
          if (sectionIndex === -1) {
            // Jika centang content TERPILIH dan section-nya belum pernah didaftar:
            // Daftarkan dulu section tsb, dan content (yang ini) jadi indeks tunggal pertama.
            classData.push({ section_id: sectionId, contents_id: [contentId] });
          } else {
            // Jika section sudah didaftar, cukup PUSH tambahkan content_id ini ke dalam list.
            let contents = [...classData[sectionIndex].contents_id];
            if (!contents.includes(contentId)) {
              contents.push(contentId);
            }
            classData[sectionIndex] = { ...classData[sectionIndex], contents_id: contents };
          }
        } else {
          if (sectionIndex !== -1) {
            // Jika centang content DIHAPUS, FILTER dan buang contentId bersangkutan dari list
            let contents = classData[sectionIndex].contents_id.filter(id => id !== contentId);

            if (contents.length === 0) {
              // Apabila ini adalah content/materi terakhir, berarti section jadi kosong total, hapus section!
              classData.splice(sectionIndex, 1);
            } else {
              // Jika masih ada sisa konten tercentang di section ini, update ulang ke array di section
              classData[sectionIndex] = { ...classData[sectionIndex], contents_id: contents };
            }
          }
        }
      }

      /* ===================================================================================== */
      /* 4. CLEAN-UP DAN PUSH KEMBALI KE HIERARKI STATE UTAMA
      /* ===================================================================================== */

      // Apabila dari action A atau B array section (data) jadi bersih nol / kosong,
      // buang parent dari array tetingginya (Class/Mata pelajaran) juga.
      if (classData.length === 0) {
        newState.splice(classIndex, 1);
      } else {
        // Jika tidak kosong, perbarui array modifikasi tsb ke object terluarnya (newState).
        newState[classIndex] = { ...newState[classIndex], data: classData };
      }

      return newState; // React akan melakukan render ulang dengan state yang bersih ini
    });
  }

  const handleClose = () => {
    setSelectedContent(initialContent) // Revert state back to before modal was opened
    onClose()
    window.$(`#${id}`).modal('hide')
  }

  const handleSave = () => {
    onSave(selectedContent)
    window.$(`#${id}`).modal('hide')
  }

  /* ========================================== Effects ========================================= */
  useEffect(() => {
    selectedContentRef.current = selectedContent
  }, [selectedContent])


  useEffect(() => {
    const modal = window.$(`#${id}`)
    const handleShow = () => {
      // Simpan backup state terakhir sebelum mulai mengedit di modal
      setInitialContent(selectedContentRef.current)
    }

    modal.on('show.bs.modal', handleShow)

    return () => {
      modal.off('show.bs.modal', handleShow)
    }
  }, [id])

  useEffect(() => {
    if (selectedData?.id) {
      getSectionsByClassId(selectedData?.id)
    }
  }, [selectedData])

  /* ========================================== Output ========================================== */
  return (
    <div
      className="modal fade"
      id={id}
      data-backdrop="static"
      data-keyboard="false"
      tabIndex={-1}
      role="dialog"
      aria-labelledby={id}
      aria-hidden="true"
    >
      <div className={`modal-xl modal-dialog modal-dialog-centered`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{selectedData?.title}</h5>
            <button
              ref={closeBtn}
              type="button"
              className="close text-danger"
              data-dismiss="modal"
              aria-label="Close"
              onClick={() => handleClose()}
            >
              <i className="feather icon-x" />
            </button>
          </div>
          <div className="modal-body">
            <ul className="list-unstyled">
              {isLoading ? (
                <LoadingSpinner />
              ) : sections?.length !== 0 ? (
                sections?.map((section, index) => (
                  <ContentSubmapelItem
                    key={section?.id}
                    item={section}
                    index={index}
                    sections={sections}
                    classId={selectedData?.id}
                    selectedContent={selectedContent}
                    setContentSelected={setContentSelected}
                  />
                ))
              ) : (
                <div className="row">
                  <NotFound
                    icon="fas fa-file"
                    text="Tidak ada materi"
                  />
                </div>
              )}
            </ul>
          </div>
          <div className="modal-footer p-2">
            <button
              type="button"
              onClick={() => handleClose()}
              className="btn btn-outline-danger mr-2"
            >
              Batal
            </button>

            <button
              onClick={() => handleSave()}
              type="button"
              className="btn btn-success"
            >
              Simpan
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
